import React, { lazy, Suspense, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import Footer from '../components/Footer'

const ScientificHeroCanvas = lazy(() => import('../components/hero/ScientificHeroCanvas'))

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(
    () =>
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  )

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    const onChange = () => setReduced(mq.matches)
    onChange()
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])

  return reduced
}

const timelineProjects = [
  {
    id: 'scoreline',
    name: 'Scoreline',
    domain: 'withscoreline.com',
    href: 'https://withscoreline.com/',
    logoSrc: '/project-scoreline-icon.svg',
    rangeLabel: 'May 2026 – present',
    status: 'active' as const,
  },
  {
    id: 'second-insights',
    name: '2ND /NSIGHTS',
    domain: '2ndinsights.com',
    href: 'https://2ndinsights.com/',
    logoSrc: '/project-2ndinsights-icon.png',
    rangeLabel: 'Jan 2026 – present',
    status: 'active' as const,
  },
  {
    id: 'cogo',
    name: 'Cogo',
    domain: 'cogo.tools',
    href: 'https://cogo.tools',
    logoSrc: '/cogo_rounded.png',
    rangeLabel: 'June 2023 – Jan 2024',
    status: 'archived' as const,
  },
]

const Home: React.FC = () => {
  const [headerScrolled, setHeaderScrolled] = useState(false)
  const reducedMotion = usePrefersReducedMotion()

  useEffect(() => {
    const onScroll = () => {
      setHeaderScrolled(window.scrollY > 8)
    }
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  return (
    <div className="flex min-h-screen flex-col bg-white text-accent">
      <header
        className={`sticky top-0 z-50 shrink-0 bg-white transition-[border-color] duration-200 ${
          headerScrolled ? 'border-b border-gray-200/90' : 'border-b border-transparent'
        }`}
      >
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-6 px-6">
          <Link
            to="/"
            className="shrink-0 overflow-hidden rounded-md ring-1 ring-black/[0.06]"
            aria-label="Syentist home"
          >
            <img
              src="/syentist_logo_square.png"
              alt="Syentist"
              width={42}
              height={42}
              className="block size-[42px] object-cover object-center sm:size-11 sm:min-h-[2.75rem] sm:min-w-[2.75rem]"
              loading="eager"
            />
          </Link>

          <div className="flex justify-end">
            <a
              href="mailto:hi@syentist.com"
              className="inline-flex h-10 w-[84px] items-center justify-center rounded-full bg-accent text-sm font-medium text-white transition-opacity hover:opacity-90 whitespace-nowrap"
            >
              Contact
            </a>
          </div>
        </div>
      </header>

      <main className="flex shrink-0 flex-col">
        <section
          className="relative flex min-h-[calc(100svh-4rem)] flex-col items-center justify-center overflow-hidden bg-white px-6 py-14 text-center sm:py-16"
          aria-label="Hero section"
        >
          <div
            className="pointer-events-none absolute inset-0 z-0 h-full min-h-full w-full overflow-hidden bg-white"
            aria-hidden
          >
            {reducedMotion ? (
              <div className="hero-lab-static absolute inset-0" />
            ) : (
              <Suspense
                fallback={<div className="hero-lab-static absolute inset-0" aria-hidden />}
              >
                <div className="absolute inset-0 h-full min-h-full w-full" aria-hidden>
                  <ScientificHeroCanvas className="h-full w-full" />
                </div>
              </Suspense>
            )}
            <div
              className="absolute inset-0 z-[1] bg-gradient-to-b from-white/48 via-white/62 to-white/88"
              aria-hidden
            />
          </div>
          <div className="relative z-10 mx-auto max-w-3xl">
            <h1 className="text-[1.625rem] leading-tight font-bold tracking-tight text-accent sm:text-4xl md:text-[2.75rem] md:leading-[1.1]">
              Venture studio building AI-powered products for underserved niches.
            </h1>
            <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-gray-600 sm:text-lg">
              We discover, build, and scale micro-SaaS businesses in overlooked markets—from insurance tools to luxury resale—using rapid experimentation.
            </p>
          </div>
        </section>

        <section
          aria-labelledby="projects-heading"
          className="bg-gray-50/90 px-6 py-20 md:py-28"
        >
          <div className="mx-auto max-w-2xl lg:max-w-3xl">
            <h2
              id="projects-heading"
              className="text-xs font-semibold uppercase tracking-[0.2em] text-gray-500"
            >
              Projects
            </h2>

            <div className="relative mx-auto mt-12 max-w-2xl md:mt-16">
              <div
                className="pointer-events-none absolute bottom-0 left-4 top-[11px] z-0 w-px -translate-x-1/2 bg-gray-300 md:top-[12.5px]"
                aria-hidden
              />
              <ol className="relative z-[1] list-none">
                {timelineProjects.map((p, idx) => {
                  const isLast = idx === timelineProjects.length - 1
                  return (
                    <li
                      key={p.id}
                      className={`grid grid-cols-[2rem_minmax(0,1fr)] items-start gap-x-4 gap-y-3 md:gap-x-5 md:gap-y-4 ${
                        isLast ? 'pb-0' : 'pb-12 md:pb-16'
                      }`}
                    >
                      <div className="relative z-[1] flex justify-center pt-[7px] md:pt-[8px]" aria-hidden>
                        <span
                          className={`size-2 shrink-0 rounded-full border-2 border-white md:size-[9px] ${
                            p.status === 'active'
                              ? 'bg-primary shadow-[0_0_0_1px_rgba(148,163,184,0.85)]'
                              : 'bg-white shadow-[0_0_0_1px_rgba(148,163,184,0.9)] ring-1 ring-inset ring-gray-400'
                          }`}
                        />
                      </div>

                      <p className="min-w-0 text-xs font-medium uppercase tracking-wider text-gray-500">
                        {p.rangeLabel}
                      </p>

                      <div className="col-start-2 min-w-0">
                        <a
                          href={p.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`group block rounded-2xl bg-white p-6 shadow-[0_8px_30px_-12px_rgba(22,24,37,0.08)] transition-[box-shadow,transform,opacity] hover:-translate-y-0.5 hover:shadow-[0_14px_44px_-14px_rgba(22,24,37,0.16)] md:p-7 ${
                            p.status === 'active'
                              ? ''
                              : 'opacity-[0.9] hover:opacity-100'
                          }`}
                        >
                          <div className="flex flex-col gap-6 md:flex-row md:items-stretch md:justify-between md:gap-8">
                            <div className="flex flex-1 items-start gap-5 md:gap-6">
                              <div className="flex h-16 w-16 shrink-0 items-center justify-center md:h-20 md:w-20">
                                <img
                                  src={p.logoSrc}
                                  alt={p.name}
                                  className="h-full w-full select-none object-contain object-center transition-opacity"
                                  loading="lazy"
                                />
                              </div>
                              <div className="min-w-0 flex-1 pt-px">
                                <div className="flex flex-wrap items-center gap-x-2 gap-y-2">
                                  <h3 className="text-xl font-semibold tracking-tight text-accent md:text-[1.3125rem]">
                                    {p.name}
                                  </h3>
                                  <span
                                    className={`rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider md:text-[11px] ${
                                      p.status === 'active'
                                        ? 'bg-primary/20 text-accent'
                                        : 'bg-gray-100 text-gray-700'
                                    }`}
                                  >
                                    {p.status === 'active' ? 'Active' : 'Archived'}
                                  </span>
                                </div>
                                <p className="mt-1 text-sm font-medium text-gray-500">{p.domain}</p>
                              </div>
                            </div>
                            <span
                              className="hidden items-center justify-end pr-1 text-lg text-accent/25 md:flex md:h-auto md:flex-col md:justify-center md:self-stretch md:pl-2 md:text-2xl md:leading-none md:opacity-75 group-hover:text-accent/50"
                              aria-hidden
                            >
                              ↗
                            </span>
                          </div>
                        </a>
                      </div>
                    </li>
                  )
                })}
              </ol>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  )
}

export default Home
